import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { View, TouchableOpacity, ImageBackground, BackHandler, StatusBar, Platform, Dimensions } from 'react-native';
import { WebView as OriginalWebView } from 'react-native-webview';

// import { ProgressView } from '@react-native-community/progress-view';
import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction, has as _has, isUndefined as _isUndefined } from 'lodash';
import PropTypes from 'prop-types';

import { Icon, IconTypes, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { Images, Colors, useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import FrameworkTestKeys from '../../FrameworkTestKeys';
import { WebViewTrackingManager } from '../../Utils/TrackingManagers';

import styles from './WebView.Styles';

function setUpRef(webViewRef, forwardedRef) {
  if (_isFunction(forwardedRef)) {
    forwardedRef(webViewRef);
  } else if (!_isUndefined(forwardedRef)) {
    if (_has(forwardedRef, 'current')) {
      forwardedRef.current = webViewRef;
    } else {
      forwardedRef = webViewRef;
    }
  }
}

const WebView = forwardRef(
  (
    {
      withReload,
      renderReloadIcon,
      renderCloseIcon,
      headerStyle,
      url,
      statusBarColor,
      onClose,
      style,
      withBackIcon,
      renderHeaderTitle,
      ...props
    },
    forwardedRef,
  ) => {
    let webView = null;
    const [title, setTitle] = useState(url);
    const [color, setColor] = useState('rgba(230, 0, 0, 0.1)');
    const [progress, setProgress] = useState(0.3);
    const [loading, setLoading] = useState(false);
    const [canBack, setCanBack] = useState(false);
    const _navState = useRef({});
    const theme = useTheme();
    useEffect(() => {
      WebViewTrackingManager.trackView();
    }, []);
    useEffect(() => {
      const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
        if (canBack) {
          webView.goBack();
          return true;
        }
        return false;
      });
      return function cleanup() {
        backHandler.remove();
      };
    }, []);

    const _handleCloseWebView = () => {
      if (onClose && _isFunction(onClose)) {
        onClose();
      }
    };

    // listen for changes on url and update them to webview
    useEffect(() => {
      if (Platform.OS === 'web' && Dimensions.get('window').width > 700) {
        setTitle(url)
      }
    }, [url])

    return (
      <ImageBackground
        source={Images.background_grey_bg_video}
        resizeMode="cover"
        style={styles.containerStyle(theme)}
        testID={FrameworkTestKeys.WebView}
      >
        {statusBarColor && (
          <StatusBar translucent barStyle={statusBarColor} backgroundColor="transparent" />
        )}
        {props.navigation ? (
          <React.Fragment>
            <View style={[styles.headerStyle(theme), headerStyle]}>
              {withBackIcon && canBack && (
                <TouchableOpacity
                  disabled={!props.navigation}
                  onPress={() => webView.goBack()}
                  style={styles.touchableBack}
                  testID={FrameworkTestKeys.WebViewBackBtn}
                >
                  <Icon
                    type={IconTypes.image}
                    name={Images.ic_arrow_left}
                    style={[styles.iconStyle, { tintColor: theme.colors.textColor }]}
                    size={25}
                  />
                </TouchableOpacity>
              )}
              {withReload ? (
                <TouchableOpacity
                  disabled={!props.navigation}
                  onPress={() => webView.reload()}
                  style={styles.touchableRefresh}
                  testID={FrameworkTestKeys.WebViewReloadBtn}
                >
                  {_isFunction(renderReloadIcon) ? (
                    renderReloadIcon(theme)
                  ) : (
                    <Icon
                      type={IconTypes.image}
                      name={Images.ic_refresh}
                      style={[styles.iconStyle, { tintColor: theme.colors.textColor }]}
                      size={25}
                    />
                  )}
                </TouchableOpacity>
              ) : null}
              {_isFunction(renderHeaderTitle) ? (
                renderHeaderTitle(_navState.current, theme)
              ) : (
                <VFText
                  style={styles.textHeader(theme)}
                  i18nKey={title}
                  numberOfLines={1} />
              )}
              <TouchableOpacity
                style={styles.touchableClose(theme)}
                disabled={!props.navigation}
                onPress={() => _handleCloseWebView()}
              >
                {_isFunction(renderCloseIcon) ? (
                  renderCloseIcon(theme)
                ) : (
                  <Icon
                    type={IconTypes.image}
                    name={Images.ic_close}
                    style={{ tintColor: theme.colors.textColor }}
                    size={25}
                    testID={FrameworkTestKeys.WebViewCloseBtn}
                  />
                )}
              </TouchableOpacity>
            </View>
          </React.Fragment>
        ) : null}

        <OriginalWebView
          ref={_ref => {
            webView = _ref;
            setUpRef(_ref, forwardedRef);
          }}
          source={{ uri: url }}
          style={[styles.containerStyle, style]}
          onLoadStart={() => {
            setColor(Colors.red);
            setLoading(true);
          }}
          onLoadProgress={({ nativeEvent }) => {
            setProgress(nativeEvent.progress);
          }}
          onLoadEnd={() => {
            setLoading(false);
          }}
          onNavigationStateChange={navState => {
            setTitle(navState.title || navState.url);
            setCanBack(navState.canGoBack);
            _navState.current = navState;
          }}
          {...props}
        />
      </ImageBackground>
    );
  },
);

WebView.displayName = 'WebView';

WebView.defaultProps = {
  withReload: true,
  withBackIcon: true,
};

WebView.propTypes = {
  url: PropTypes.string,
  style: ViewPropTypes.style,
  navigation: PropTypes.object,
  onClose: PropTypes.func,
  withReload: PropTypes.bool,
  renderReloadIcon: PropTypes.func,
  renderCloseIcon: PropTypes.func,
  headerStyle: ViewPropTypes.style,
  statusBarColor: PropTypes.string,
  withBackIcon: PropTypes.bool,
  renderHeaderTitle: PropTypes.func,
};

export { setUpRef };
export default WebView;
